import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import { getImagesDoc, supportedImageFormatsDoc, vtpmDoc } from 'app/docLinks'
import { useAppSelector } from 'app/store'
import ActionRequiredModalTrigger, {
  ActionRequiredIssue,
} from 'core/components/ActionRequiredModalTrigger'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { MessageTypes } from 'core/components/notifications/model'
import SelectableCard from 'core/components/SelectableCard'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import RadioFields from 'core/components/validatedForm/radio-fields'
import TextField from 'core/components/validatedForm/TextField'
import Radio from 'core/elements/input/Radio'
import LargeFileDrop from 'core/elements/LargeFileDrop'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useUpdateActionsWithProgress from 'core/hooks/useUpdateActionsWithProgress'
import { Route } from 'core/plugins/route'
import { useToast } from 'core/providers/ToastProvider'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { isAdminRole } from 'core/utils/rbac'
import { routeManager } from 'core/utils/routes'
import { listBlueprints } from 'openstack/components/infrastructure/actions'
import { imageLibraryStoresSelector } from 'openstack/components/infrastructure/selectors'
import { trackEvent } from 'utils/tracking'
import ServiceUnhealthyInfo from '../common/ServiceUnhealthyInfo'
import { copyImage, createImage, deleteImage, importImage, uploadImage } from './actions'
import DiskFormatPicklist from './DiskFormatPicklist'
import { rescueImageTooltip } from './helpers'
import { addImageMemberAction } from './imageMembers'
import OsPicklist from './OsPicklist'
import TenantAccessField from './TenantAccessField'
import VisibilityPicklist from './VisibilityPicklist'

const useStyles = makeStyles<Theme>((theme) => ({
  link: {
    width: 'max-content',
  },
  checkboxField: {
    width: 'fit-content',
  },
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    alignItems: 'start',
    width: '100%',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  radioOptions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  cardContent: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  cardContentMinHeight: {
    minHeight: '60px',
  },
  icon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  warning: {
    color: theme.components.badge.error.color,
    cursor: 'pointer',
    marginBottom: 16,

    '& > span': {
      color: theme.components.badge.error.color,
    },
  },
}))

interface Props {
  addRoute: Route
  actionRequiredIssues: ActionRequiredIssue[]
  glanceHostNotReachable: boolean
  imageLibraryPresent: boolean
}

export enum ImageUploadMethod {
  FILE = 'file',
  URL = 'url',
}

export const imageUploadRadioOptions = [
  {
    value: ImageUploadMethod.FILE,
    label: 'Upload Image File',
    info: 'Select an image file to upload.',
  },
  {
    value: ImageUploadMethod.URL,
    label: 'Upload Image via URL.',
    info: 'Select an image that is accessible for download via a URL.',
  },
]

export const vtpmRadioOptions = [
  {
    value: 'tpm-tis',
    label: 'TPM Interface Specification (TIS)',
  },
  {
    value: 'tpm-crb',
    label: 'Command-Response Buffer (CRB)',
  },
]

export default function AddImageModal({
  addRoute,
  actionRequiredIssues,
  glanceHostNotReachable,
  imageLibraryPresent,
}: Props) {
  const { history, match } = useReactRouter()
  const { tab } = match.params
  const classes = useStyles()
  // PCD-8206: admins share with any tenant; non-admins only with tenants they belong to.
  // TenantAccessField uses this to pick the full vs user-scoped tenant source.
  const session = useAppSelector((state) => state[sessionStoreKey]) as SessionState
  const isAdmin = isAdminRole(session)
  const defaultParams = {
    name: '',
    method: 'file',
    containerFormat: 'bare',
    diskFormat: 'qcow2',
    url: '',
    visibility: 'private',
    selectedTenants: [] as string[],
    protected: false,
    enableVTPM: false,
    vtpmModel: 'tpm-tis',
    enableRescue: false,
    osType: null,
  }
  const [fileData, setFileData] = useState(null)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [abortController, setAbortController] = useState(null)
  const [imageId, setImageId] = useState(null)
  const [memberError, setMemberError] = useState<string | null>(null)

  // Number of configured image stores, from the blueprint's imageLibraryStores (cached in Redux).
  // When more than one, a file upload is copied to every store after it lands in the default
  // store. (FR-012)
  useListAction(listBlueprints)
  const storeCount = useAppSelector(imageLibraryStoresSelector).length

  const showToast = useToast()

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { update: addMember } = useUpdateAction(addImageMemberAction)

  const {
    update: deleteFn,
    updating: deletingItem,
    error: deleteError,
  } = useUpdateAction(deleteImage)
  const {
    update: create,
    updating: creating,
    error,
    reset: resetCreate,
  } = useUpdateAction(createImage)

  const { state, update: upload } = useUpdateActionsWithProgress()
  const {
    update: importFn,
    updating: importing,
    error: importError,
    reset: resetImport,
  } = useUpdateAction(importImage)
  // Separate action from importImage: copy-image fan-out must hit the admin/host endpoint,
  // web-download import (importFn) hits the public endpoint. See Glance.importImageOnHost. (FR-014)
  const { update: copyFn, reset: resetCopy } = useUpdateAction(copyImage)

  const reset = (isRedirect = true) => {
    setImageId(null)
    setParams(defaultParams)
    setFileData(null)
    setMemberError(null)
    resetCreate()
    resetImport()
    resetCopy()
    if (isRedirect) history.push(routeManager.routes.openstack.images.path({ tab }))
  }

  const submitForm = useCallback(async () => {
    if (params.method === ImageUploadMethod.FILE && !fileData) {
      trackEvent(`Add Image - Validation Failed`, { failedFields: ['fileData'] })
      setFormSubmitted(true)
      return
    }

    const body = {
      name: params.name,
      container_format: params.containerFormat ? params.containerFormat : undefined,
      disk_format: params.diskFormat ? params.diskFormat : undefined,
      protected: params.protected,
      visibility: params.visibility,
      hw_tpm_version: params.enableVTPM ? '2.0' : undefined,
      hw_tpm_model: params.enableVTPM ? params.vtpmModel : undefined,
      hw_rescue_device: params.enableRescue ? 'disk' : undefined,
      hw_rescue_bus: params.enableRescue ? 'scsi' : undefined,
      os_type: params.osType,
    }
    // For a URL (web-download) import on a library with more than one store, ask Glance to write
    // the downloaded image to every configured store. The public glance-api pod hands the download
    // off to the image-library host, which expands all_stores to all of its stores, so this single
    // request replicates the image everywhere without a separate copy-image step (unlike the file
    // upload path). all_stores_must_succeed is false so the default store's copy is kept even if a
    // slower store lags behind, matching the multi-store design's resilience guidance. With a single
    // store we send neither flag, leaving the request unchanged. (FR-015)
    const importBody = {
      method: {
        name: 'web-download',
        uri: params.url,
      },
      ...(storeCount > 1 && { all_stores: true, all_stores_must_succeed: false }),
    }
    const { success, response } = await create({ body })
    if (!success) {
      return
    }
    const imageId = response?.id
    setImageId(imageId)

    let hasMemberError = false
    if (params.visibility === 'shared' && params.selectedTenants.length > 0) {
      for (const projectId of params.selectedTenants) {
        const result = await addMember({ imageId, projectId })
        if (!result?.success) {
          const status = result?.error?.response?.status || result?.error?.err?.response?.status
          if (status === 409) continue
          if (status === 403) {
            setMemberError('Cannot share image with your own project.')
            hasMemberError = true
          }
        }
      }
    }

    try {
      const { success: uploadSuccess } =
        params.method === ImageUploadMethod.FILE
          ? await upload(imageId, uploadImage, {
              id: imageId,
              body: fileData,
              abortController,
              name: params.name,
              imagePayload: body,
            })
          : await importFn({ id: imageId, body: importBody })

      if (!uploadSuccess) {
        await deleteFn({ id: imageId })
        return
      }

      // The file landed in the default store. When more than one store is configured,
      // copy it to every store so the image is highly available. Glance returns 202
      // (copy started, not finished), and the image is already usable in the default
      // store — so a trigger failure is non-fatal: notify and keep the image. (FR-012, FR-013)
      if (params.method === ImageUploadMethod.FILE && storeCount > 1) {
        const copyBody = {
          method: { name: 'copy-image' },
          all_stores: true,
          all_stores_must_succeed: true,
        }
        // The image is already active in the default store, so a copy-trigger failure is
        // non-fatal. Keep this in its own try/catch: whether importFn rejects or just returns
        // success=false, we notify and KEEP the image — it must never reach the outer catch
        // that deletes the image. (FR-013)
        try {
          const { success: copySuccess } = await copyFn({ id: imageId, body: copyBody })
          if (!copySuccess) {
            throw new Error('copy-image import did not start')
          }
        } catch (copyError) {
          showToast(
            'Image uploaded, but copying it to all stores could not be started. ' +
              'You can retry the copy later.',
            MessageTypes.warning,
          )
        }
      }

      // Close the modal for URL import only if there were no member errors to show
      if (params.method === ImageUploadMethod.URL && !hasMemberError) {
        reset(true)
      }
    } catch (error) {
      await deleteFn({ id: imageId })
      resetCreate()
      resetImport()
    }
  }, [
    params,
    fileData,
    addMember,
    create,
    upload,
    importFn,
    copyFn,
    deleteFn,
    resetCreate,
    resetImport,
    storeCount,
    showToast,
  ])

  const handleClose = useCallback(() => {
    if (params.method === ImageUploadMethod.FILE && fileData) {
      setShowConfirmationDialog(true)
    } else {
      // close the modal without showing the confirmation dialog
      setShowConfirmationDialog(false)
      reset()
    }
  }, [params.method, fileData, reset])

  const handleConfirm = useCallback(() => {
    setShowConfirmationDialog(false)
    reset()
  }, [reset])

  // Close the modal when the image is created and image status is updating
  useEffect(() => {
    if (imageId && state[imageId]?.updating) {
      reset()
    }
  }, [state, imageId])

  useEffect(() => {
    const controller = new AbortController()
    setAbortController(controller)
    reset(false)
  }, [])

  return (
    <>
      <ModalForm
        route={addRoute}
        title={`Add Image`}
        onSubmit={submitForm}
        onClose={handleClose}
        submitting={creating || importing || deletingItem}
        error={error || importError || deleteError}
        submitTitle={`Add Image`}
      >
        {memberError && <ErrorMessage>{memberError}</ErrorMessage>}
        {imageLibraryPresent && actionRequiredIssues.length ? (
          <ActionRequiredModalTrigger
            issues={actionRequiredIssues}
            modalTitle="Action Required"
            modalIntroText="The following actions are required to access the Image Library Endpoint:"
            trigger={({ openWithEvent }) => (
              <Text variant="subtitle2" className={classes.warning} onClick={openWithEvent}>
                <FontAwesomeIcon className={classes.icon}>info-circle</FontAwesomeIcon>
                {glanceHostNotReachable
                  ? 'Image Library Host Not Reachable - Action Required'
                  : 'Action Required'}{' '}
                ({actionRequiredIssues.length})
              </Text>
            )}
          />
        ) : null}
        <ServiceUnhealthyInfo action="Image Creation" imageLibrary />
        <FormFieldSection title="Image Selection" alignContentLeft>
          <div className={classes.radioOptions}>
            {imageUploadRadioOptions.map((option) => (
              <SelectableCard
                id={option.value}
                key={option.value}
                onClick={() => getParamsUpdater('method')(option.value)}
                active={params?.method === option.value}
              >
                <div className={classes.cardContent}>
                  <Radio checked={params?.method === option.value} />
                  <div className={classes.cardContentMinHeight}>
                    <Text variant="caption1">{option.label}</Text>
                    <Text variant="body2">{option.info}</Text>
                  </div>
                </div>
              </SelectableCard>
            ))}
          </div>
          {params.method === ImageUploadMethod.FILE ? (
            <>
              <Text variant="body2">
                <b>Note:</b> Images larger than <b>10GB</b> are recommended to be imported via CLI
                rather than through file upload. When uploading larger files, ensure you have stable
                network connectivity to prevent any issues.
              </Text>
              <ExternalLink className={classes.link} url={getImagesDoc}>
                Download popular images
              </ExternalLink>
              <LargeFileDrop
                id="fileDrop"
                value={fileData}
                onChange={(value) => {
                  setFileData(value)
                  setFormSubmitted(false)
                }}
                onChangeName={(name) => {
                  if (params.name) {
                    return
                  }
                  updateParams({ name })
                }}
                fileTypes={[
                  '.img',
                  '.iso',
                  '.bare',
                  '.ovf',
                  '.ova',
                  '.vhd',
                  '.vhdx',
                  '.vmdk',
                  '.raw',
                  '.qcow2',
                ]}
              />
              {!fileData && formSubmitted && <ErrorMessage>Upload a file</ErrorMessage>}
            </>
          ) : (
            <TextField
              id="url"
              label="Image Download URL"
              onChange={getParamsUpdater('url')}
              value={params.url}
              required={params.method === ImageUploadMethod.URL}
            />
          )}
        </FormFieldSection>
        <FormFieldSection title="Image Settings">
          <div className={classes.settingsGrid}>
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              required
            />
            <PicklistField
              DropdownComponent={OsPicklist}
              id="osType"
              onChange={getParamsUpdater('osType')}
              value={params.osType}
              label="Operating System"
              required
            />
            <PicklistField
              DropdownComponent={DiskFormatPicklist}
              id="diskFormat"
              onChange={getParamsUpdater('diskFormat')}
              value={params.diskFormat}
              label="Disk Format"
              required
            />
            <PicklistField
              DropdownComponent={VisibilityPicklist}
              id="visibility"
              onChange={getParamsUpdater('visibility')}
              value={params.visibility}
              label="Visibility"
            />
            <div className={classes.fullWidth}>
              <Text variant="body2">
                Choose the disk format and other settings for the image. See{' '}
                <ExternalLink url={supportedImageFormatsDoc}>here</ExternalLink> for details about
                supported disk formats, and <ExternalLink url={vtpmDoc}>here</ExternalLink> for
                details about virtual TPM.
              </Text>
            </div>
            {params.visibility === 'shared' && (
              <div className={classes.fullWidth}>
                <TenantAccessField
                  value={params.selectedTenants}
                  onChange={getParamsUpdater('selectedTenants')}
                  isAdmin={isAdmin}
                />
              </div>
            )}
            <div className={`${classes.fullWidth} ${classes.checkboxField}`}>
              <CheckboxField
                id="protected"
                label="Disable Delete (Protected)"
                onChange={getParamsUpdater('protected')}
                value={params.protected}
                info="Images that are marked as 'protected' can not be deleted."
              />
            </div>
            <div className={`${classes.fullWidth} ${classes.checkboxField}`}>
              <CheckboxField
                id="enableRescue"
                label="Use for rescuing VMs"
                onChange={getParamsUpdater('enableRescue')}
                value={params.enableRescue}
                info={rescueImageTooltip}
                tooltipOffset={{
                  vertical: 0,
                  horizontal: -180,
                }}
              />
            </div>
            <div className={`${classes.fullWidth} ${classes.checkboxField}`}>
              <CheckboxField
                id="enableVTPM"
                label="Enable Virtual TPM 2.0 "
                onChange={getParamsUpdater('enableVTPM')}
                value={params.enableVTPM}
                info={
                  <>
                    Enable vTPM for virtual machines created using this image. Read{' '}
                    <ExternalLink url={vtpmDoc}>here</ExternalLink> for more info
                  </>
                }
              />
            </div>
            {params.enableVTPM && (
              <div className={classes.fullWidth}>
                <RadioFields
                  title="Virtual TPM Model"
                  id="vtpmModel"
                  options={vtpmRadioOptions}
                  value={params.vtpmModel}
                  onChange={getParamsUpdater('vtpmModel')}
                />
              </div>
            )}
          </div>
        </FormFieldSection>
      </ModalForm>
      {showConfirmationDialog && (
        <ConfirmationDialog
          open
          title="Discard Image?"
          text="Are you sure you want to discard the image?"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmationDialog(false)}
        />
      )}
    </>
  )
}
